/**
 * Browser geolocation helpers (OpenStreetMap migration).
 *
 * These replace the ad-hoc `navigator.geolocation` + `ipapi.co` blocks that
 * used to live inside every Google-Maps component. Nothing here talks to
 * Google, coordinates feed straight into Leaflet / Nominatim.
 */

/**
 * Ask the browser for the current position. Resolves to `{ lat, lng }` or
 * `null` if the user denies it, the API is missing, or it times out, callers
 * must never be blocked by geolocation.
 */
export function getBrowserLocation({
  enableHighAccuracy = true,
  timeout = 10000,
  maximumAge = 30000,
} = {}) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => done({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => done(null),
        { enableHighAccuracy, timeout, maximumAge }
      );
    } catch {
      done(null);
    }

    // Hard fallback in case the browser never fires either callback.
    setTimeout(() => done(null), timeout + 1000);
  });
}

/** Coarse IP-based location fallback (no key, public endpoint). */
export async function getIpLocation() {
  try {
    const res = await fetch("https://ipapi.co/json");
    const data = await res.json();
    if (typeof data?.latitude === "number" && typeof data?.longitude === "number") {
      return { lat: data.latitude, lng: data.longitude };
    }
  } catch {
    /* ignore, caller handles null */
  }
  return null;
}

/**
 * Best-effort current location: tries the precise browser API first, then
 * falls back to a coarse IP lookup. Resolves to `{ lat, lng }` or `null`.
 */
export async function getCurrentLocation(opts) {
  const precise = await getBrowserLocation(opts);
  if (precise) return precise;
  return getIpLocation();
}

// Same sessionStorage slot MapLayers' auto-locate writes its fix to.
const SESSION_FIX_KEY = "jaaga_maps_user_fix";

/**
 * The visitor's location ONLY if we can have it without asking: a fix already
 * taken this session, or a fresh one when the browser's permission is already
 * "granted". Never triggers a permission prompt. Resolves to `{ lat, lng }` or
 * `null`.
 */
export async function getKnownLocation({ timeout = 4000 } = {}) {
  try {
    const o = JSON.parse(sessionStorage.getItem(SESSION_FIX_KEY));
    if (Number.isFinite(o?.lat) && Number.isFinite(o?.lng)) {
      return { lat: o.lat, lng: o.lng };
    }
  } catch {
    /* storage unavailable, fall through to the permission check */
  }
  try {
    const status = await navigator?.permissions?.query?.({ name: "geolocation" });
    if (status?.state !== "granted") return null;
  } catch {
    return null; // Permissions API missing (older Safari): don't risk a prompt
  }
  return getBrowserLocation({ timeout, maximumAge: 5 * 60 * 1000 });
}
